import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Star } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const Stars = ({ value, onSelect, size = 14 }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        onClick={() => onSelect?.(n)}
        className={onSelect ? "cursor-pointer" : "cursor-default"}
        aria-label={`${n} star`}
      >
        <Star size={size} className={n <= value ? "fill-gold text-gold" : "text-zinc-300"} />
      </button>
    ))}
  </div>
);

export const Reviews = ({ productId }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState({ reviews: [], average: null, count: 0 });
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [posting, setPosting] = useState(false);

  const load = () =>
    api.get(`/products/${productId}/reviews`).then((r) => setData(r.data)).catch(() => {});

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!user) {
      navigate("/login");
      return;
    }
    setPosting(true);
    try {
      await api.post(`/products/${productId}/reviews`, { rating, comment });
      toast.success("Review posted — thank you");
      setComment("");
      setRating(5);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setPosting(false);
    }
  };

  return (
    <section className="py-24 border-t border-zinc-200" data-testid="reviews-section">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-16">
        <div className="md:col-span-4">
          <p className="text-xs uppercase tracking-[0.5em] text-gold mb-6">Reviews</p>
          <div className="flex items-baseline gap-4">
            <span className="font-serif text-7xl text-zinc-900" data-testid="reviews-average">{data.average ?? "—"}</span>
            <div>
              <Stars value={Math.round(data.average || 0)} size={16} />
              <p className="text-sm text-zinc-500 mt-2">{data.count} {data.count === 1 ? "review" : "reviews"}</p>
            </div>
          </div>
          <form onSubmit={submit} className="mt-12 space-y-5" data-testid="review-form">
            <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Share your experience</p>
            <Stars value={rating} onSelect={setRating} size={20} />
            <textarea
              rows={3}
              required
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="What did you love?"
              data-testid="review-comment-input"
              className="w-full bg-zinc-100 border border-zinc-200 text-zinc-900 text-sm px-4 py-3 focus:outline-none focus:border-gold/60 transition-colors duration-300"
            />
            <button
              type="submit"
              disabled={posting}
              data-testid="review-submit-btn"
              className="bg-gold text-white text-xs uppercase tracking-[0.3em] px-8 py-3.5 hover:bg-champagne transition-colors duration-300 disabled:opacity-50"
            >
              {user ? (posting ? "Posting…" : "Post Review") : "Sign In to Review"}
            </button>
          </form>
        </div>
        <div className="md:col-span-8 space-y-8">
          {data.reviews.length === 0 ? (
            <p className="font-serif italic text-2xl text-zinc-400 pt-8" data-testid="reviews-empty">Be the first to review.</p>
          ) : (
            data.reviews.map((r) => (
              <motion.div key={r.id} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="border-b border-zinc-100 pb-8" data-testid={`review-${r.id}`}>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-pink-50 border border-pink-100 flex items-center justify-center font-serif text-lg text-gold">
                    {r.user_name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-zinc-800">{r.user_name}</p>
                    <p className="text-xs text-zinc-400">{new Date(r.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</p>
                  </div>
                  <div className="ml-auto"><Stars value={r.rating} /></div>
                </div>
                <p className="text-zinc-600 text-sm leading-relaxed mt-4">{r.comment}</p>
              </motion.div>
            ))
          )}
        </div>
      </div>
    </section>
  );
};
